import { $, html, View } from 'rune-ts';
import { SkuIntlType } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/types.ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import klass from './StockVolumeModal.module.scss';
import common_klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/styles/common.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { NumberTextInput } from '../../../../../../../../../../shared/rune-component/Input/NumberTextInput.ts';
import { pureInputVariant } from '../../../../../../../../../../shared/rune-component/Input/PureInput.ts';
import { StudioButton } from '@services/MarppleShop/shared/components/atoms/StudioButton/StudioButton.ts';
import { entries, every, filter, pipe, reduce, values } from '@fxts/core';
import { doIf } from '@services/MarppleShop/shared/util';
import { unEscaper } from '@shared/utils/escape.ts';

export type StockVolumes = 'width' | 'height' | 'length' | 'weight';

export interface VolumeInputType extends View {
  getState(): Record<StockVolumes, number>;
  getIndex(): number;
}

export interface StockVolumeModalData {
  index: number;
  name: string;
  value?: SkuIntlType;
  min?: number;
  max?: number;
  // eslint-disable-next-line no-use-before-define
  onRegister?: (view: VolumeInputType) => void;
  // eslint-disable-next-line no-use-before-define
  onClose?: (view: VolumeInputType) => void;
}

export class StockVolumeModalBody extends View<StockVolumeModalData> implements VolumeInputType {
  private state: Record<StockVolumes, number> = {
    width: this.data?.value?.width || 0,
    height: this.data?.value?.height || 0,
    length: this.data?.value?.length || 0,
    weight: this.data?.value?.weight || 0,
  };

  private closeButton = new StudioButton({
    label: '취소',
    color: 'line_gray',
    size: 'free',
    custom_klass: `${klass.action}`,
    onClick: () => {
      this.onVolumeOptionChange('width', 0);
      this.onVolumeOptionChange('height', 0);
      this.onVolumeOptionChange('length', 0);
      this.onVolumeOptionChange('weight', 0);
      this.onClose?.(this);
    },
  });

  private registerButton = new StudioButton({
    label: '등록',
    color: 'sky',
    size: 'free',
    custom_klass: `${klass.action}`,
    is_disabled: !this.validateAllVolume(),
    onClick: () => {
      this.onRegister?.(this);
      this.onClose?.(this);
    },
  });

  private readonly onRegister = this.data.onRegister;
  private readonly onClose = this.data.onClose;

  protected override template() {
    return html`
      <div class="${klass.body}">
        <div class="${klass.top}">
          ${doIf(
            () => html`
              <div class="${klass.name}">
                <h3 class="${shareFont('16_bold', '14_bold')}">품목</h3>
                <span class="${shareFont('16_medium', '14_medium')}">${unEscaper(this.data.name)}</span>
              </div>
            `,
            !!this.data.name,
          )}

          <div class="${klass.volumes}">
            <div class="${klass.volume}">
              <h4 class="${shareFont('14_bold', '12_bold')}">가로</h4>

              <div class="${klass.input} ${shareFont('14_medium', '12_medium')}">
                ${new NumberTextInput({
                  variant: pureInputVariant.studio_editor_stock_option,
                  index: this.data.index,
                  name: this.data.name,
                  value: this.state.width,
                  min: this.data.min,
                  max: this.data.max,
                  onFocusout: (view) => {
                    this.onVolumeOptionChange('width', Number(view.getValue().replaceAll(',', '')));

                    if (this.validateVolumeCalc()) {
                      this.onChangeVolumeText();
                    }

                    this.registerButton.setDisable(!this.validateAllVolume());
                  },
                })}
                <span>cm</span>
              </div>
            </div>

            <div class="${klass.volume}">
              <h4 class="${shareFont('14_bold', '12_bold')}">세로</h4>

              <div class="${klass.input} ${shareFont('14_medium', '12_medium')}">
                ${new NumberTextInput({
                  variant: pureInputVariant.studio_editor_stock_option,
                  index: this.data.index,
                  name: this.data.name,
                  value: this.state.length,
                  min: this.data.min,
                  max: this.data.max,
                  onFocusout: (view) => {
                    this.onVolumeOptionChange('length', Number(view.getValue().replaceAll(',', '')));

                    if (this.validateVolumeCalc()) {
                      this.onChangeVolumeText();
                    }

                    this.registerButton.setDisable(!this.validateAllVolume());
                  },
                })}
                <span>cm</span>
              </div>
            </div>
          </div>

          <div class="${klass.volumes}">
            <div class="${klass.volume}">
              <h4 class="${shareFont('14_bold', '12_bold')}">높이</h4>

              <div class="${klass.input} ${shareFont('14_medium', '12_medium')}">
                ${new NumberTextInput({
                  variant: pureInputVariant.studio_editor_stock_option,
                  index: this.data.index,
                  name: this.data.name,
                  value: this.state.height,
                  min: this.data.min,
                  max: this.data.max,
                  onFocusout: (view) => {
                    this.onVolumeOptionChange('height', Number(view.getValue().replaceAll(',', '')));

                    if (this.validateVolumeCalc()) {
                      this.onChangeVolumeText();
                    }

                    this.registerButton.setDisable(!this.validateAllVolume());
                  },
                })}
                <span>cm</span>
              </div>
            </div>

            <div class="${klass.volume}">
              <h4 class="${shareFont('14_bold', '12_bold')}">중량</h4>

              <div class="${klass.input} ${shareFont('14_medium', '12_medium')}">
                ${new NumberTextInput({
                  variant: pureInputVariant.studio_editor_stock_option,
                  index: this.data.index,
                  name: this.data.name,
                  value: this.state.weight,
                  min: this.data.min,
                  max: this.data.max,
                  onFocusout: (view) => {
                    this.onVolumeOptionChange('weight', Number(view.getValue().replaceAll(',', '')));

                    if (this.validateVolumeCalc()) {
                      this.onChangeVolumeText();
                    }

                    this.registerButton.setDisable(!this.validateAllVolume());
                  },
                })}
                <span>g</span>
              </div>
            </div>
          </div>
        </div>

        <div class="${klass.bottom}">
          <div class="${klass.left}">
            <span class="${shareFont('14_medium', '12_medium')}">부피 무게 계산</span>
            <h3 class="${klass.variable} ${shareFont('20_bold', '18_bold')}">${this.getVolume()}g</h3>
          </div>

          <div class="${klass.right}">${this.closeButton} ${this.registerButton}</div>
        </div>
      </div>
    `;
  }

  private onVolumeOptionChange(key: StockVolumes, value: number) {
    this.state = {
      ...this.state,
      [key]: value,
    };
  }

  private onChangeVolumeText() {
    $(this.element()).find(`.${klass.variable}`)?.setTextContent(`${this.getVolume().toLocaleString()}g`);
  }

  private getVolume() {
    return pipe(
      [this.state.width, this.state.height, this.state.length],
      reduce((acc, cur) => acc * cur),
      (acc) => Math.floor(acc / 6),
    );
  }

  private validateVolumeCalc() {
    return pipe(
      entries(this.state),
      filter(([key]) => key !== 'weight'),
      every((state) => Boolean(state)),
    );
  }

  private validateAllVolume() {
    return pipe(
      values(this.state),
      every((state) => Boolean(state)),
    );
  }

  public getState() {
    return this.state;
  }

  public getIndex() {
    return this.data.index;
  }
}

export class StockVolumeModal {
  static create(data: StockVolumeModalData) {
    const stockVolumeeModal = new Modal({
      title_options: {
        use_close_btn: false,
      },
      backdrop_not_close: true,
      full_width: true,
      full_body_height: true,
      isOpen: false,
      body: html``,
      size: 'medium',
      modal_klass: klass.modal,
      body_klass: klass.modal_body,
    });

    return stockVolumeeModal
      .setTitle(html`
        <div class="${klass.title}">
          <h2 class="${shareFont('20_bold', '16_bold')}">체적정보 등록</h2>
          ${new Tooltip({
            id: `sku_${data.index}`,
            content: html`
              <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                <div class="${common_klass.tooltip_content_divider}">
                  <span>·</span>
                  <span>상품 최종 패키지의 가로 x 세로 x 높이 / 6000 = 부피 무게 계산</span>
                </div>
                <div class="${common_klass.tooltip_content_divider}">
                  <span>·</span>
                  <span>배송비 측정은 부피 무게와 실제 중량 중 더 높은 값으로 결정됩니다.</span>
                </div>
              </div>
            `,
            size: 'x_small',
            variant: 'studio',
          })}
        </div>
      `)
      .setBody(new StockVolumeModalBody({ ...data, onClose: () => stockVolumeeModal.close() }));
  }
}
